import { I18n } from "@assets/strings";
import React from "react";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";
import { BigPlusButton } from "@ui/components/buttons/bigPlusButton";
import {
	AssetManagerIcon,
	CameraIcon,
	FileIcon,
	ImageIcon,
	PdfIcon,
	SubtitleIcon,
	TextIcon,
	YoutubeIcon,
} from "@ui/components/icons";
import { row } from "@ui/components/layout";
import { AssetBrowserModal } from "@ui/components/media/assetBrowser/assetBrowserModal";
import { FileUpload } from "@ui/components/mediaEditor/fileUpload";
import { Menu } from "@ui/components/menus/menu";
import { useModalTools } from "@ui/modals/modalContext";
import { gap } from "@ui/styles/utils";
import { useEditorController } from "@ui/pages/lms/article/articleEditorPage/articleEditorController/editorControllerContext";

interface AddSectionMenuProps extends React.HTMLAttributes<HTMLDivElement> {
	currentlyEditing: boolean;
	asFirst: boolean;
}
export const AddSectionMenu: React.FC<AddSectionMenuProps> = ({ currentlyEditing, asFirst, ...htmlProps }) => {
	const articleController = useEditorController();
	const { createModal } = useModalTools();

	return (
		<Menu
			{...htmlProps}
			closeOnSelect
			renderMenuContent={(close) => (
				<SectionTypes>
					<SectionRow
						onClick={() => {
							return articleController.addSection("SUBTITLE");
						}}
					>
						<SubtitleIcon /> <div>{I18n.t("article.editor.section.subtitle.new")}</div>
					</SectionRow>
					<SectionRow
						onClick={() => {
							return articleController.addSection("TEXT");
						}}
					>
						<TextIcon /> <div>{I18n.t("article.editor.section.text.new")}</div>
					</SectionRow>
					<Separator />
					<SectionRow
						onClick={() => {
							createModal((closeModal) => (
								<AssetBrowserModal
									close={(assets) => {
										closeModal();
										const asset = assets?.get(0);
										if (asset) {
											if (asset.mimeType.match("image/*")) {
												articleController.addSection("IMAGE", asset.id);
												articleController.addAsset(asset);
											} else if (asset.mimeType.match("video/*")) {
												articleController.addSection("VIDEO", asset.id);
												articleController.addAsset(asset);
											} else if (asset.mimeType === "application/pdf") {
												articleController.addSection("PDF", asset.id);
												articleController.addAsset(asset);
											} else {
												articleController.addSection("ATTACHMENT", asset.id, asset.displayName);
												articleController.addAsset(asset);
											}
										}
									}}
								/>
							));
						}}
					>
						<AssetManagerIcon /> <div>{I18n.t("asset_manager.title")}</div>
					</SectionRow>
					<Separator />
					<SectionRow onClick={(e) => e.stopPropagation()}>
						<FileUpload
							onUpload={(media) => {
								articleController.addSection("IMAGE", media.id);
								articleController.addAsset(media);
								close();
							}}
						>
							{(_, loading) => (
								<ImageUploadContent>
									{loading ? <ClipLoader size={16} /> : <ImageIcon />}{" "}
									<div>{I18n.t("article.editor.section.image.new")}</div>
								</ImageUploadContent>
							)}
						</FileUpload>
					</SectionRow>
					<SectionRow onClick={(e) => e.stopPropagation()}>
						<FileUpload
							onUpload={(media) => {
								articleController.addSection("PDF", media.id);
								articleController.addAsset(media);
								close();
							}}
							type="pdf"
						>
							{(_, loading) => (
								<ImageUploadContent>
									{loading ? <ClipLoader size={16} /> : <PdfIcon />}{" "}
									<div>{I18n.t("article.editor.section.document.new")}</div>
								</ImageUploadContent>
							)}
						</FileUpload>
					</SectionRow>
					<SectionRow
						onClick={(e) => {
							e.stopPropagation();
						}}
					>
						<FileUpload
							type="video"
							onUploadStart={(taskId) => {
								close();
								articleController.addLoadingSection(taskId);
							}}
							onUpload={(media, _, taskId) => {
								articleController.addOrReplaceSection("VIDEO", media.id, taskId);
								articleController.addAsset(media);
							}}
						>
							{() => (
								<ImageUploadContent>
									<CameraIcon /> <div>{I18n.t("article.editor.section.video.new")}</div>
								</ImageUploadContent>
							)}
						</FileUpload>
					</SectionRow>
					<SectionRow onClick={(e) => e.stopPropagation()}>
						<FileUpload
							type="file"
							onUpload={(media, fileName) => {
								articleController.addSection("ATTACHMENT", media.id, fileName);
								articleController.addAsset(media);

								close();
							}}
						>
							{(_, loading) => (
								<ImageUploadContent>
									{loading ? <ClipLoader size={16} /> : <FileIcon />}{" "}
									<div>{I18n.t("article.editor.section.attachment.new")}</div>
								</ImageUploadContent>
							)}
						</FileUpload>
					</SectionRow>
					<Separator />
					<SectionRow onClick={() => articleController.addSection("VIDEO")}>
						<YoutubeIcon /> <div>{I18n.t("article.editor.section.youtube.new")}</div>
					</SectionRow>
				</SectionTypes>
			)}
			menuWrapperStyle={{
				left: "50%",
				right: "initial",
				top: "50%",
				transform: "translate(-50%, 16px)",
			}}
		>
			<BigPlusButton label={I18n.t("article.editor.section.add") + (asFirst ? "*" : "")} />
		</Menu>
	);
};

const SectionTypes = styled.div`
	${gap(16)}
	min-width: 200px;
`;

const SectionRow = styled.div`
	cursor: pointer;
	display: flex;
	align-items: center;
	${({ theme }) => theme.textStyles.text};
	div {
		transition: transform 0.2s ease-in-out;
	}
	&:hover div:not(:first-child) {
		transform: translateX(5px);
	}
	svg {
		margin-right: 5px;
		width: 18px;
	}
`;

const ImageUploadContent = styled.div`
	${row(5, "flex-start", "center")};
`;

const Separator = styled.div`
	height: 1px;
	background-color: ${({ theme }) => theme.colors.lightergray};
`;
